import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { FEE_VALIDATION } from '../../../utils/validations/validations';
import InitialValuesForm from '../../../constants/initialValues';
import CustomDatePicker from '../../../components/date-picker/customDatePicker';
import InputText from '../../../components/input-text/inputText';
import Button from '../../../components/button';
import {
  useAdvanceBoolean,
  useBoolean,
  useGetSecFeeList,
  useGetTablePreferences,
  usePostSecFee,
  usePostTablePreferences,
  usePutSecFee,
  useWealthlaneAwardApi,
} from '../../../hooks';
import { ModalStatusType, TableParamsType } from '../../../types/common';
import { feeListHeader } from '../../../constants/table-headers/feeListHeader';
import Spinners from '../../../components/spinner';
import { EditedRow } from '../../../packages/table-builder/components/Table';
import AlertPopup from '../../../components/alert-popup';
import { FeeService } from '@wealthlane/api-services/wealthlane-award-service';
import { toast } from 'react-toastify';
import ToastComponent from '../../../components/toast';
import { useQueryClient } from 'react-query';
import { PageHeader } from '../company-profile/profile.styled';
import Heading from '../../../components/typography';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import FeeModal from './feeModal';
import {
  useDynamicTable,
  DynamicTable,
  withDynamicTableContext,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import { axiosInstance } from '../../../services/apiInterceptor';

const initialValues = {
  data: InitialValuesForm.fee,
  id: '',
};

const Fee = () => {
  const { handleRequest } = useWealthlaneAwardApi();
  const { tableInfo, errors, setErrors } = useDynamicTable();
  const queryClient = useQueryClient();
  const {
    value: isSaveOpen,
    setFalse: closeSaveModal,
    setTrue: openSaveModal,
    customValue,
    setCustomValue,
  } = useAdvanceBoolean<string>(false);
  const {
    value: isConfirmOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
    customValue: confirmCustomValue,
    setCustomValue: setConfirmCustomValue,
  } = useAdvanceBoolean<string>(false);
  const [resolveSave, setResolveSave] = useState<(() => void) | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const [modalStatus, setModalStatus] = useState<ModalStatusType>('Add');
  const [initialData, setInitialData] = useState(initialValues);
  const [editLoading, setEditLoading] = useState(false);
  const [headerList, setHeaderList] = useState<string[]>([]);

  const {
    data: secFeeList,
    status,
    isLoading: secFeeListLoading,
  } = useGetSecFeeList(handleRequest, tableInfo);
  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'FEE_TABLE',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });
  const { data: tablePreferences } = useGetTablePreferences(handleRequest, 'Fee');

  useEffect(() => {
    tablePreferences != null && setHeaderList([...tablePreferences]);
  }, [tablePreferences]);

  const { mutate: postTablePreference, isLoading: tablePrefLoading } =
    usePostTablePreferences(handleRequest);

  const handleSavePreference = (value: string[]): void => {
    const prefPayload = {
      tableName: 'Fee',
      preferences: value,
    };
    postTablePreference(prefPayload);
  };
  const { mutateAsync, isLoading: editFeeLoading } = usePutSecFee(
    handleRequest,
    closeSaveModal,
    (message, newErrors) => {
      setErrors([...errors, ...newErrors]);
    },
    setEditLoading
  );
  const tableHeaderList = feeListHeader({
    actions: schemaData?.columns,
    setConfirmCustomValue,
    openConfirmModal,
  })
    .filter((item) => item.isSubHeader)
    .map((item) => {
      // console.log('item', item);

      return {
        label: item.Header,
        value: item.accessor,
      };
    });

  const grantHeader = (): any[] => {
    return feeListHeader({
      actions: schemaData?.columns,
      setConfirmCustomValue,
      openConfirmModal,
    }).filter((item) => item.isSubHeader === false || headerList.includes(item.accessor));
  };

  const schema = React.useMemo(
    () => ({
      columns: feeListHeader({
        actions: schemaData?.columns,
        setConfirmCustomValue,
        openConfirmModal,
      }),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );

  const data = React.useMemo(() => secFeeList?.items || [], [secFeeList, secFeeListLoading]);
  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' />;

  const handleSave = async (editedData: EditedRow[]) => {
    openSaveModal();
    setCustomValue(editedData as any);
    await new Promise<void>((resolve) => {
      setResolveSave(() => resolve);
    });
  };

  const mapToPayloadFormat = (customValue: Record<string, any>[]) => {
    return {
      id: customValue?.[0]?.id,
      effectiveDate: customValue?.[0]?.effectiveDate,
      rate: customValue?.[0]?.rate,
    };
    // return customValue;
  };

  const editSECFee = async () => {
    try {
      const payload = mapToPayloadFormat(customValue);
      await mutateAsync(payload as any);
      resolveSave && resolveSave();
    } catch (error) {
    } finally {
      setCustomValue(null);
      closeSaveModal();
    }
  };

  const onPrimaryClick = (values: Record<string, any>): void => {
    deleteBroadcastMessageById(confirmCustomValue);
  };

  const deleteBroadcastMessageById = (id: string): void => {
    setModalLoading(true);
    handleRequest(FeeService.deleteApiAwardApiAppFeeSecFee(id))
      .then((data: any) => {
        if (data != null) {
          toast.success(<ToastComponent label={'Success'} message={'SEC Fee deleted.'} />);
          void queryClient.invalidateQueries('getSecFeeList');
          setModalLoading(false);
          closeConfirmModal();
        }
      })
      .catch(() => {
        setModalLoading(false);
        closeConfirmModal();
      })
      .finally(() => {
        setModalLoading(false);
        closeConfirmModal();
      });
  };

  const renderRightToolbarButton = () => {
    return (
      <Button
        // as='Create'
        title='Add SEC Fee'
        variant='primary'
        icon={'add_circle_outline'}
        id='feeAddSecFeeButton'
        onClick={() => {
          setModalStatus('Add');
          openModal();
          setInitialData(initialValues);
        }}
      />
    );
  };

  return (
    <div id='feePageContainer'>
      <PageHeader data-page='secFee' id='feePageHeader'>
        <Heading variant='h3' title='Fee' id='feePageTitle' />
        <Heading variant='body-s' title={`List of SEC fees.`} id='feePageDescription' />
      </PageHeader>

      <AnimationWrapper id='feeAnimationWrapper'>
        {isModalOpen && (
          <FeeModal
            id='feeModal'
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            initialData={initialData}
            handleRequest={handleRequest}
            setModalStatus={setModalStatus}
            loading={false}
            modalStatus={modalStatus}
          />
        )}
      </AnimationWrapper>

      <DynamicTable
        id='feeDataTable'
        schema={schema}
        data={data}
        loading={status}
        totalCounts={secFeeList?.totalCount ?? 10}
        onSave={handleSave}
        isSaveLoading={editLoading}
        components={{ renderRightToolbarButton }}
        tableColumnList={tableHeaderList}
        selectedColumnList={headerList}
        tablePreferenceLoading={tablePrefLoading}
        handleColumnSave={handleSavePreference}
      />
      {isSaveOpen && (
        <AlertPopup
          id='feeSaveAlert'
          isShow={isSaveOpen}
          closeModal={closeSaveModal}
          customTitle={
            <div className='text-center w-full' id='feeSaveAlertTitle'>
              Are you sure you want to Save?
            </div>
          }
          handleSubmit={editSECFee}
          loading={editLoading}
        />
      )}
      {isConfirmOpen && (
        <AlertPopup
          id='feeDeleteAlert'
          isShow={isConfirmOpen}
          closeModal={closeConfirmModal}
          customTitle={
            <div className='text-center w-full' id='feeDeleteAlertTitle'>
              Are you sure you want to Delete?
            </div>
          }
          handleSubmit={onPrimaryClick}
          loading={modalLoading}
        />
      )}
    </div>
  );
};

export default withDynamicTableContext(Fee, false, axiosInstance);
